import axios from 'axios';

import {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useState,
} from 'react';

import { Environment } from '../../constants/environments';
import { HttpVerb } from '../../constants/httpVerbs';
import { logger } from '../../utils/logger';
import { useApiService } from '../useApiService';
import { useLoaderScreenContext } from './loader-screen.context';

export interface ResaleListingGroup {
  groupName: string;
  ticketName: string;
  guestTypeId: number;
  count: number;
  finalPrice: number;
  uuid: string;
  offerId: string | null;
  /** Forward-compat: populated when backend starts returning eventId in /grouped response. */
  eventId?: number;
}

export type ResaleListingContextProps = {
  groups: ResaleListingGroup[];
  loading: boolean;
  hasError: boolean;
  fetchListingGrouped: (identifier: string) => Promise<void>;
};

const ResaleListingContext = createContext({} as ResaleListingContextProps);

function ResaleListingProvider({ children }: { children: ReactNode }) {
  const [groups, setGroups] = useState<ResaleListingGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const { request } = useApiService();
  const { toggleLoader } = useLoaderScreenContext();

  const fetchListingGrouped = useCallback(async (identifier: string): Promise<void> => {
    if (!identifier) {
      logger.warn('[ResaleListingContext] fetchListingGrouped: identifier is required');
      return;
    }

    try {
      setLoading(true);
      setHasError(false);

      const resourcePath = `/resale/event/${encodeURIComponent(identifier)}/grouped?apikey=${Environment.API_KEY}`;

      const response = await request<null, { detail: { groups: ResaleListingGroup[] } }>(
        { requestOptions : { resourcePath }, type : HttpVerb.GET },
        { baseUrl : Environment.API_GATEWAY_URL }
      );

      const responseGroups = response?.detail?.groups ?? [];
      setGroups(responseGroups);
      logger.info(`[ResaleListingContext] Loaded ${responseGroups.length} groups for identifier: ${identifier}`);
    } catch (err) {
      const isNotFound = axios.isAxiosError(err) && err.response?.status === 404;
      if (isNotFound) {
        setGroups([]);
      } else {
        logger.error('[ResaleListingContext] Failed to fetch grouped listing', { identifier, error : err });
        setHasError(true);
        setGroups([]);
      }
    } finally {
      setLoading(false);
      toggleLoader({ show : false });
    }
  }, [request, toggleLoader]);

  const shared: ResaleListingContextProps = {
    groups,
    loading,
    hasError,
    fetchListingGrouped,
  };

  return (
    <ResaleListingContext.Provider value={shared}>
      {children}
    </ResaleListingContext.Provider>
  );
}

ResaleListingProvider.displayName = 'ResaleListingProvider';

export const useResaleListingContext = (): ResaleListingContextProps => {
  const context = useContext(ResaleListingContext);
  if (!context || Object.keys(context).length === 0) {
    throw new Error(
      'useResaleListingContext must be used within a ResaleListingProvider. ' +
      'Wrap the consumer inside <ResaleListingProvider>.'
    );
  }
  return context;
};

export { ResaleListingContext, ResaleListingProvider };
